import { DatePipe } from '@angular/common';
import { CredentialExpirationRule, ExpirationOption } from './credentialExpirationRule.model';
import { CredentialNumberSettings } from './credentialNumberSettings.model';
import { CredentialRenewalRule } from './credentialRenewalRule.model';
import moment from 'moment';
import { CredentialApplicationDefinitionModel } from './credential-application-definition.model';
import { CredentialApplicationDefinitionType } from './CredentialApplicationDefinitionType';
import { CredentialPrimaryRenewalModel } from './credentialPrimaryRenewal.model';

export class CredentialDefinitionModel {
  id: string;
  name: string;
  status: CredentialDefinitionStatus;
  type: CredentialDefinitionType;
  credentialApplications: Record<
    CredentialApplicationDefinitionType,
    CredentialApplicationDefinitionModel
  >;
  numberSettings: CredentialNumberSettings;
  expirationRule: CredentialExpirationRule;
  renewalRule: CredentialRenewalRule;
  primaryRenewalForm: CredentialPrimaryRenewalModel;
  private datePipe: DatePipe = new DatePipe(moment.locale());

  constructor(data?: Partial<CredentialDefinitionModel>) {
    if (!data) return;
    this.id = data.id;
    this.name = data.name;
    this.status = data.status;
    this.type = data.type;
    this.credentialApplications = {
      Initial: data.credentialApplications?.Initial
        ? new CredentialApplicationDefinitionModel(data.credentialApplications.Initial)
        : null,
      Renewal: data.credentialApplications?.Renewal
        ? new CredentialApplicationDefinitionModel(data.credentialApplications.Renewal)
        : null,
      Reinstatement: data.credentialApplications?.Reinstatement
        ? new CredentialApplicationDefinitionModel(data.credentialApplications.Reinstatement)
        : null,
      Specialty: data.credentialApplications?.Specialty
        ? new CredentialApplicationDefinitionModel(data.credentialApplications.Specialty)
        : null
    };
    this.numberSettings = new CredentialNumberSettings(data.numberSettings);
    this.expirationRule = new CredentialExpirationRule(data.expirationRule);
    this.renewalRule = new CredentialRenewalRule(data.renewalRule);
    this.primaryRenewalForm = new CredentialPrimaryRenewalModel(data.primaryRenewalForm);
  }

  findApplicationById = (applicationDefinitionId: string): CredentialApplicationDefinitionModel => {
    if (this.credentialApplications.Initial?.id === applicationDefinitionId) {
      return this.credentialApplications.Initial;
    }
    if (this.credentialApplications.Renewal?.id === applicationDefinitionId) {
      return this.credentialApplications.Renewal;
    }
    if (this.credentialApplications.Reinstatement?.id === applicationDefinitionId) {
      return this.credentialApplications.Reinstatement;
    }
    if (this.credentialApplications.Specialty?.id === applicationDefinitionId) {
      return this.credentialApplications.Specialty;
    }
    return null;
  };

  updateAllApplications = (
    actionOnApplication: (application: CredentialApplicationDefinitionModel) => void
  ) => {
    if (this.credentialApplications.Initial) {
      actionOnApplication(this.credentialApplications.Initial);
    }
    if (this.credentialApplications.Renewal) {
      actionOnApplication(this.credentialApplications.Renewal);
    }
    if (this.credentialApplications.Reinstatement) {
      actionOnApplication(this.credentialApplications.Reinstatement);
    }
    if (this.credentialApplications.Specialty) {
      actionOnApplication(this.credentialApplications.Specialty);
    }
  };

  markAsActive() {
    this.status = CredentialDefinitionStatus.Active;
  }

  markAsInactive() {
    this.status = CredentialDefinitionStatus.Inactive;
  }

  get isActive() {
    return this.status === CredentialDefinitionStatus.Active;
  }

  public GetCredentialDateDescriptionsDetails(): {
    expiration: string;
    expirationExtension: string;
    renewalBegins: string;
    renewalEnds: string;
    reinstatement: string;
    minimumDays: string;
  } {
    let result = {
      expiration: '',
      expirationExtension: '',
      renewalBegins: '',
      renewalEnds: '',
      reinstatement: '',
      minimumDays: ''
    };
    result.minimumDays = 'No minimum length';
    if (this.expirationRule?.canExpire != true) {
      result.expiration = 'Does not expire';
    } else {
      switch (this.expirationRule.expirationOption) {
        case ExpirationOption.YearsFromIssueDate:
          result.expiration = `Expires ${this.expirationRule.yearsUntilExpiration} years from issue date`;
          break;
        case ExpirationOption.EveryYearOnDate:
          result.expiration = `Every year starting on ${this.datePipe.transform(
            this.expirationRule.expirationDate,
            'MMM d'
          )}`;
          break;
        case ExpirationOption.EvenYearsOnDate:
          result.expiration = `Even years starting on ${this.datePipe.transform(
            this.expirationRule.expirationDate,
            'MMM d'
          )}`;
          break;
        case ExpirationOption.OddYearsOnDate:
          result.expiration = `Odd years starting on ${this.datePipe.transform(
            this.expirationRule.expirationDate,
            'MMM d'
          )}`;
          break;
        case ExpirationOption.CustomRule:
          result.expiration = this.expirationRule.customRenewalRule;
          break;
      }
      if (this.expirationRule.hasMinimumLength) {
        result.minimumDays = `${this.expirationRule.minimumDays} day${
          this.expirationRule.minimumDays > 1 ? 's' : ''
        }`;
      }
    }
    if (this.renewalRule != null && this.renewalRule?.canRenew) {
      result.expirationExtension = `Extends expiration date ${this.renewalRule.yearsToRenew} year${
        this.renewalRule.yearsToRenew > 1 ? 's' : ''
      }`;
      result.renewalBegins = `${this.renewalRule.daysBeforeExpiration ?? 0} day${
        this.renewalRule.daysBeforeExpiration > 1 ? 's' : ''
      } before credential expiration`;
      result.renewalEnds = `${this.renewalRule.daysAfterExpiration ?? 0} day${
        this.renewalRule.daysAfterExpiration > 1 ? 's' : ''
      } after credential expiration`;
    } else {
      result.expirationExtension = 'Does not extend expiration date';
      result.renewalBegins = 'Does not renew';
      result.renewalEnds = 'Does not renew';
    }
    result.reinstatement = this.credentialApplications.Reinstatement
      ? `Can be reinstated ${this.renewalRule.daysAfterExpiration} days after expiration`
      : 'Does not accept reinstatements';
    return result;
  }

  public GetCredentialDateDescriptionsSummary(): {
    expiration: string;
    renewal: string;
    reinstatement: string;
  } {
    let result = { expiration: '', renewal: '', reinstatement: '' };
    if (this.expirationRule?.canExpire != true) {
      result.expiration = 'does not expire';
    } else {
      switch (this.expirationRule.expirationOption) {
        case ExpirationOption.YearsFromIssueDate:
          result.expiration = `expires ${this.expirationRule.yearsUntilExpiration} years from issue date`;
          break;
        case ExpirationOption.EveryYearOnDate:
          result.expiration = `every year starting on ${this.datePipe.transform(
            this.expirationRule.expirationDate,
            'MMM d'
          )}`;
          break;
        case ExpirationOption.EvenYearsOnDate:
          result.expiration = `even years starting on ${this.datePipe.transform(
            this.expirationRule.expirationDate,
            'MMM d'
          )}`;
          break;
        case ExpirationOption.OddYearsOnDate:
          result.expiration = `odd years starting on ${this.datePipe.transform(
            this.expirationRule.expirationDate,
            'MMM d'
          )}`;
          break;
        case ExpirationOption.CustomRule:
          result.expiration = this.expirationRule.customRenewalRule;
          break;
      }
    }
    result.renewal =
      this.renewalRule?.canRenew != true
        ? 'does not accept renewals'
        : `renewal available ${this.renewalRule.daysBeforeExpiration} days before expiration and ${
            this.renewalRule != null ? this.renewalRule.daysAfterExpiration : 0
          } days after expiration, extends expiration date ${this.renewalRule.yearsToRenew} years`;

    result.reinstatement = this.credentialApplications.Reinstatement
      ? `can be reinstated ${this.renewalRule.daysAfterExpiration} days after expiration`
      : 'does not accept reinstatements';
    return result;
  }
}

export enum CredentialDefinitionStatus {
  Active = 'Active',
  Inactive = 'Inactive'
}
export enum CredentialDefinitionType {
  Person = 'Person',
  Team = 'Team'
}
